import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    ToggleButtonGroup,
    ToggleButton,
    Stack,
    Avatar,
    Chip,
    LinearProgress,
    Tooltip as MuiTooltip,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    TextField,
    Button,
} from '@mui/material';
import {
    ArrowUpward,
    ArrowDownward,
    Refresh,
    TrendingUp,
    LocalShipping,
    Assessment,
    MonetizationOn,
    AccessTime,
    Clear,
} from '@mui/icons-material';
import {
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import {
    fetchDashboardSummary,
    selectDashboardState,
    setPreset,
    setCustomRange,
    clearCustomRange,
} from '../../store/slices/dashboardSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { DashboardPreset } from '../../services/dashboardService';

const presetOptions: { label: string; value: DashboardPreset }[] = [
    { label: 'Last 7 Days', value: 'last_7_days' },
    { label: 'Last 30 Days', value: 'last_30_days' },
    { label: 'Last 6 Months', value: 'last_6_months' },
    { label: 'Last 1 Year', value: 'last_1_year' },
];

const pieColors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336', '#00bcd4'];

const numberFormatter = new Intl.NumberFormat('en-PK', {
    maximumFractionDigits: 0,
});

const currencyFormatter = new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    maximumFractionDigits: 0,
});

const percentageFormatter = (value: number) =>
    `${(value ?? 0).toFixed(1)}%`;

const Dashboard = () => {
    const { theme } = useTheme();
    const dispatch = useAppDispatch();
    const { filters, data, loading, error } = useAppSelector(selectDashboardState);

    const selectedPreset = filters.preset;
    const [fromInput, setFromInput] = useState(filters.fromDate ?? '');
    const [toInput, setToInput] = useState(filters.toDate ?? '');

    useEffect(() => {
        if (selectedPreset === 'custom' && (!filters.fromDate || !filters.toDate)) {
            return;
        }

        dispatch(
            fetchDashboardSummary({
                preset: selectedPreset,
                from: filters.fromDate ?? undefined,
                to: filters.toDate ?? undefined,
            })
        );
    }, [dispatch, selectedPreset, filters.fromDate, filters.toDate]);

    useEffect(() => {
        if (filters.preset === 'custom') {
            setFromInput(filters.fromDate ?? '');
            setToInput(filters.toDate ?? '');
        }
    }, [filters.preset, filters.fromDate, filters.toDate]);

    const handlePresetChange = (_: React.SyntheticEvent, value: DashboardPreset | null) => {
        if (!value || value === selectedPreset) return;
        setFromInput('');
        setToInput('');
        dispatch(setPreset(value));
    };

    const handleFromInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFromInput(value);
        if (value && toInput) {
            dispatch(setCustomRange({ from: value, to: toInput }));
        }
    };

    const handleToInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setToInput(value);
        if (fromInput && value) {
            dispatch(setCustomRange({ from: fromInput, to: value }));
        }
    };

    const handleDateClear = () => {
        setFromInput('');
        setToInput('');
        dispatch(clearCustomRange());
    };

    const kpis = data?.kpis ?? [];
    const shipmentsTrend = data?.shipmentsTrend ?? [];
    const revenueTrend = data?.revenueTrend ?? [];
    const statusBreakdown = data?.statusBreakdown ?? [];
    const topCities = data?.topCities ?? [];
    const topClients = data?.topClients ?? [];
    const deliveryPerformance = data?.deliveryPerformance;

    const rangeLabel = useMemo(() => {
        if (!data?.filters) return '';
        const from = new Date(data.filters.fromDate);
        const to = new Date(data.filters.toDate);
        if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
        return `${from.toLocaleDateString('en-PK', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })} - ${to.toLocaleDateString('en-PK', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })}`;
    }, [data?.filters]);

    const isCustomSelected = selectedPreset === 'custom';

    return (
        <Box
            sx={{
                p: { xs: 2, md: 3 },
                minHeight: '100vh',
                bgcolor: theme.palette.background.default,
            }}
        >
            <Stack spacing={3}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                >
                    <Stack
                        spacing={1}
                        flex={1}
                        alignItems={{ xs: 'center', md: 'flex-start' }}
                        textAlign={{ xs: 'center', md: 'left' }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}
                        >
                            Monitor shipments and delivery performance.
                    </Typography>
                        {rangeLabel && (
                            <Chip
                                label={rangeLabel}
                                size="small"
                                sx={{
                                    width: 'fit-content',
                                    bgcolor: theme.palette.action.hover,
                                    display: { xs: 'none', sm: 'inline-flex' },
                                }}
                            />
                        )}
                    </Stack>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        flexWrap="wrap"
                        justifyContent={{ xs: 'center', sm: 'flex-end' }}
                        sx={{ width: { xs: '100%', md: 'auto' } }}
                    >
                        <ToggleButtonGroup
                            exclusive
                            value={selectedPreset}
                            size="small"
                            onChange={handlePresetChange}
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(0, 1fr))',
                                    sm: 'repeat(4, auto)',
                                },
                                gap: { xs: 1, sm: 0 },
                                '& .MuiToggleButton-root': {
                                    borderRadius: 1,
                                    minWidth: { xs: '100%', sm: 'auto' },
                                },
                            }}
                        >
                            {presetOptions.map((option) => (
                                <ToggleButton key={option.value} value={option.value}>
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            flexWrap="wrap"
                            justifyContent={{ xs: 'flex-start', sm: 'flex-start' }}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                            <TextField
                                label="From"
                                type="date"
                                size="small"
                                value={fromInput}
                                onChange={handleFromInputChange}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    width: { xs: '100%', sm: 160 },
                                }}
                            />
                            <TextField
                                label="To"
                                type="date"
                                size="small"
                                value={toInput}
                                onChange={handleToInputChange}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    width: { xs: '100%', sm: 160 },
                                }}
                            />
                            <MuiTooltip title="Clear custom range">
                                <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Clear fontSize="small" />}
                                        onClick={handleDateClear}
                                        disabled={!(fromInput || toInput || isCustomSelected)}
                                        sx={{
                                            alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                            width: { xs: '100%', sm: 'auto' },
                                            textTransform: 'none',
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </span>
                            </MuiTooltip>
                            <MuiTooltip title="Refresh data">
                                <span>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        startIcon={<Refresh fontSize="small" />}
                                        onClick={() =>
                                            dispatch(
                                                fetchDashboardSummary({
                                                    preset: selectedPreset,
                                                    from: filters.fromDate ?? undefined,
                                                    to: filters.toDate ?? undefined,
                                                })
                                            )
                                        }
                                        disabled={loading || (isCustomSelected && (!filters.fromDate || !filters.toDate))}
                                        sx={{
                                            alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                            width: { xs: '100%', sm: 'auto' },
                                            textTransform: 'none',
                                        }}
                                    >
                                        Refresh
                                    </Button>
                                </span>
                            </MuiTooltip>
                        </Stack>
                    </Stack>
                </Stack>

                {loading && (
                    <LinearProgress
                        sx={{
                            borderRadius: 999,
                            height: 4,
                        }}
                    />
                )}

                {error && (
                    <Alert severity="error">
                        {error}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(4, 1fr)',
                        },
                    }}
                >
                    {kpis.length === 0 && loading ? (
                        Array.from({ length: 4 }).map((_, index) => (
                            <Box key={`kpi-skeleton-${index}`}>
                                <Skeleton variant="rounded" height={140} />
                </Box>
                        ))
                    ) : (
                        kpis.map((kpi, index) => (
                    <motion.div
                                key={kpi.id}
                                style={{ width: '100%' }}
                                initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                    >
                                    <Card
                                        sx={{
                                            height: '100%',
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: 'none',
                                        }}
                                    >
                            <CardContent>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                        {kpi.label}
                                                    </Typography>
                                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                        {kpi.unit === 'PKR'
                                                            ? currencyFormatter.format(kpi.value)
                                                            : kpi.unit === 'percentage'
                                                                ? percentageFormatter(kpi.value)
                                                                : numberFormatter.format(kpi.value)}
                                                    </Typography>
                                                </Stack>
                                                <Avatar
                                                    sx={{
                                                        bgcolor: theme.palette.mode === 'dark'
                                                            ? theme.palette.primary.dark
                                                            : theme.palette.primary.light,
                                                        color: theme.palette.primary.contrastText,
                                                        width: 48,
                                                        height: 48,
                                                    }}
                                                >
                                                    {index === 0 && <LocalShipping />}
                                                    {index === 1 && <TrendingUp />}
                                                    {index === 2 && <Assessment />}
                                                    {index === 3 && <MonetizationOn />}
                                    </Avatar>
                                            </Stack>
                                            {kpi.delta && (
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                                                    {kpi.delta.isPositive ? (
                                                        <ArrowUpward fontSize="small" color="success" />
                                                    ) : (
                                                        <ArrowDownward fontSize="small" color="error" />
                                        )}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                            fontWeight: 600,
                                                            color: kpi.delta.isPositive
                                                                ? theme.palette.success.main
                                                                : theme.palette.error.main,
                                                        }}
                                                    >
                                                        {percentageFormatter(Math.abs(kpi.delta.value))}
                                        </Typography>
                                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                        vs previous period
                                </Typography>
                                                </Stack>
                                            )}
                            </CardContent>
                        </Card>
                    </motion.div>
                        ))
                    )}
            </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: 'none',
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Shipment Movement
                                </Typography>
                                <MuiTooltip title="Delivered, in transit, returned trend">
                                    <Chip label="Shipments" size="small" />
                                </MuiTooltip>
                            </Stack>
                            <ResponsiveContainer width="100%" height={360}>
                                <AreaChart data={shipmentsTrend}>
                                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                <XAxis 
                                        dataKey="date"
                                    stroke={theme.palette.text.secondary}
                                    fontSize={12}
                                />
                                <YAxis 
                                    stroke={theme.palette.text.secondary}
                                    fontSize={12}
                                />
                                <RechartsTooltip 
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 8,
                                    }}
                                />
                                <Legend />
                                <Area 
                                    type="monotone" 
                                    dataKey="delivered" 
                                    stackId="1" 
                                    stroke="#4caf50" 
                                    fill="#4caf50" 
                                        fillOpacity={0.5}
                                        name="Delivered"
                                />
                                <Area 
                                    type="monotone" 
                                        dataKey="inTransit"
                                    stackId="1" 
                                        stroke="#2196f3"
                                        fill="#2196f3"
                                        fillOpacity={0.4}
                                        name="In Transit"
                                />
                                <Area 
                                    type="monotone" 
                                        dataKey="returned"
                                    stackId="1" 
                                        stroke="#ff9800"
                                        fill="#ff9800"
                                        fillOpacity={0.4}
                                        name="Returned"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Paper>
                    </Box>
            </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    }}
                >
                    <Box>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: 'none',
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Status Breakdown
                        </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie
                                            data={statusBreakdown}
                                            dataKey="count"
                                            nameKey="status"
                                            innerRadius={60}
                                            outerRadius={90}
                                            paddingAngle={4}
                                        >
                                            {statusBreakdown.map((entry, index) => (
                                                <Cell key={entry.status} fill={pieColors[index % pieColors.length]} />
                                            ))}
                                        </Pie>
                                <RechartsTooltip 
                                            formatter={(value: number) => numberFormatter.format(value)}
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 8,
                                    }}
                                />
                                    </PieChart>
                                </ResponsiveContainer>
                                <Stack spacing={1}>
                                    {statusBreakdown.map((item, index) => (
                                        <Stack direction="row" spacing={1.5} alignItems="center" key={`${item.status}-${index}`}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    bgcolor: pieColors[index % pieColors.length],
                                                    borderRadius: '50%',
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ minWidth: 120 }}>
                                                {item.status}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {numberFormatter.format(item.count)}
                                            </Typography>
                                        </Stack>
                                    ))}
                                    {statusBreakdown.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            No status data for selected range.
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                    </Paper>
            </Box>
                    <Box>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                                bgcolor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: 'none',
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Delivery Performance
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                On-time percentage and average lead times.
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Avatar
                                        sx={{
                                            bgcolor: theme.palette.success.main,
                                            width: 40,
                                            height: 40,
                                        }}
                                    >
                                        <TrendingUp />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle2">
                                            On-Time Delivery Rate
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                            {deliveryPerformance
                                                ? percentageFormatter(deliveryPerformance.onTimePercentage)
                                                : '--'}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Stack spacing={1}>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                        Late Deliveries
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={deliveryPerformance ? deliveryPerformance.latePercentage : 0}
                            sx={{
                                            height: 10,
                                            borderRadius: 999,
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 999,
                                                bgcolor: theme.palette.error.main,
                                            },
                                        }}
                                    />
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <Chip
                                        icon={<AccessTime />}
                                        label={
                                            deliveryPerformance
                                                ? `${deliveryPerformance.averageDeliveryTimeDays.toFixed(1)} days avg`
                                                : 'N/A'
                                        }
                                    />
                                    <Chip
                                        color="warning"
                                        label={
                                            deliveryPerformance
                                                ? `${numberFormatter.format(deliveryPerformance.failedDeliveries)} failed`
                                                : '0 failed'
                                        }
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    }}
                >
                    <Box>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                                bgcolor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: 'none',
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Top Cities by Volume
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>City</TableCell>
                                            <TableCell align="right">Shipments</TableCell>
                                            <TableCell align="right">Revenue (PKR)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {topCities.map((city) => (
                                            <TableRow key={city.name}>
                                                <TableCell>{city.name}</TableCell>
                                                <TableCell align="right">
                                                    {numberFormatter.format(city.shipments)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {currencyFormatter.format(city.revenue)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {topCities.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No city data available.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                    <Box>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                                bgcolor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: 'none',
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Key Clients
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Client</TableCell>
                                            <TableCell align="right">Shipments</TableCell>
                                            <TableCell align="right">Revenue (PKR)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {topClients.map((client) => (
                                            <TableRow key={client.name}>
                                                <TableCell>{client.name}</TableCell>
                                                <TableCell align="right">
                                                    {numberFormatter.format(client.shipments)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {currencyFormatter.format(client.revenue)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {topClients.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No client data available.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                        Last updated {data?.lastUpdated ? new Date(data.lastUpdated).toLocaleString('en-PK') : '—'}
                    </Typography>
                    <Button
                        size="small"
                        sx={{ ml: 'auto' }}
                        onClick={() =>
                            dispatch(
                                fetchDashboardSummary({
                                    preset: selectedPreset,
                                    from: filters.fromDate ?? undefined,
                                    to: filters.toDate ?? undefined,
                                })
                            )
                        }
                        disabled={loading}
                    >
                        Refresh
                        </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

export default Dashboard; 